export interface ControlChartStatisticsInput {
	values: number[];
	withinStandardDeviation: number;
	lsl?: number | null;
	usl?: number | null;
	target?: number | null;
}

export interface ControlChartStatistics {
	sampleCount: number;
	mean: number;
	overallStandardDeviation: number;
	withinStandardDeviation: number;
	variance: number;
	skewness: number;
	kurtosis: number;
	minimum: number;
	firstQuartile: number;
	median: number;
	thirdQuartile: number;
	maximum: number;
	cp: number | null;
	cpk: number | null;
	pp: number | null;
	ppk: number | null;
	andersonDarling: number;
	andersonDarlingPValue: number;
	histogram: Array<[number, number]>;
	histogramBinWidth: number;
}

export function calculateStatisticsChartBounds(
	histogram: Array<[number, number]>,
	binWidth: number,
	lsl?: number | null,
	usl?: number | null,
): { xMinimum: number; xMaximum: number } {
	const histogramMinimum = histogram[0]![0] - binWidth / 2;
	const histogramMaximum = histogram[histogram.length - 1]![0] + binWidth / 2;
	return {
		xMinimum: Math.min(histogramMinimum, lsl ?? Infinity) - binWidth,
		xMaximum: Math.max(histogramMaximum, usl ?? -Infinity) + binWidth,
	};
}

export function calculateControlChartStatistics(input: ControlChartStatisticsInput): ControlChartStatistics {
	const values = [...input.values].sort((a, b) => a - b);
	if (values.length < 2) throw new Error("At least two records are required for control chart statistics.");
	const sampleCount = values.length;
	const mean = average(values);
	const centered = values.map((value) => value - mean);
	const sumOfSquares = centered.reduce((sum, value) => sum + value ** 2, 0);
	const variance = sumOfSquares / (sampleCount - 1);
	const overallStandardDeviation = Math.sqrt(variance);
	const moment2 = sumOfSquares / sampleCount;
	const moment3 = centered.reduce((sum, value) => sum + value ** 3, 0) / sampleCount;
	const moment4 = centered.reduce((sum, value) => sum + value ** 4, 0) / sampleCount;
	const skewness = (Math.sqrt(sampleCount * (sampleCount - 1)) / (sampleCount - 2)) * (moment3 / moment2 ** 1.5);
	const kurtosis =
		((sampleCount - 1) / ((sampleCount - 2) * (sampleCount - 3))) *
		((sampleCount + 1) * (moment4 / moment2 ** 2 - 3) + 6);
	const nearestSpecificationDistance = minimumNullable(
		input.lsl == null ? null : Math.abs(mean - input.lsl),
		input.usl == null ? null : Math.abs(mean - input.usl),
	);
	const [andersonDarling, andersonDarlingPValue] = calculateAndersonDarling(values, mean, overallStandardDeviation);
	const histogram = calculateHistogram(values, overallStandardDeviation);

	return {
		sampleCount,
		mean,
		overallStandardDeviation,
		withinStandardDeviation: input.withinStandardDeviation,
		variance,
		skewness,
		kurtosis,
		minimum: values[0]!,
		firstQuartile: quantile(values, 0.25),
		median: quantile(values, 0.5),
		thirdQuartile: quantile(values, 0.75),
		maximum: values[values.length - 1]!,
		cp:
			input.lsl != null && input.usl != null ?
				(input.usl - input.lsl) / (6 * input.withinStandardDeviation)
			:	null,
		cpk:
			nearestSpecificationDistance == null ? null : (
				nearestSpecificationDistance / (3 * input.withinStandardDeviation)
			),
		pp: input.lsl != null && input.usl != null ? (input.usl - input.lsl) / (6 * overallStandardDeviation) : null,
		ppk:
			nearestSpecificationDistance == null ? null : nearestSpecificationDistance / (3 * overallStandardDeviation),
		andersonDarling,
		andersonDarlingPValue,
		histogram: histogram.bins,
		histogramBinWidth: histogram.binWidth,
	};
}

export function normalDistributionPoints(
	mean: number,
	standardDeviation: number,
	xMin: number,
	xMax: number,
	area: number,
): Array<[number, number]> {
	if (!Number.isFinite(standardDeviation) || standardDeviation <= 0 || xMax <= xMin) return [];
	const step = (xMax - xMin) / 100;
	return Array.from({ length: 100 }, (_, index) => {
		const x = xMin + index * step;
		const z = (x - mean) / standardDeviation;
		return [x, (Math.exp(-(z ** 2) / 2) / (standardDeviation * Math.sqrt(2 * Math.PI))) * area];
	});
}

function calculateHistogram(values: number[], standardDeviation: number) {
	const range = values[values.length - 1]! - values[0]!;
	const estimatedBinWidth = 3.49 * standardDeviation * values.length ** (-1 / 3);
	const binCount = estimatedBinWidth > 0 ? Math.max(1, Math.round(range / estimatedBinWidth)) : 1;
	const padding = binCount === 1 ? Math.max(Math.abs(values[0]!) * 0.05, 0.5) : range / (2 * (binCount - 1));
	const lowerLimit = values[0]! - padding;
	const upperLimit = values[values.length - 1]! + padding;
	const binWidth = (upperLimit - lowerLimit) / binCount;
	const counts = Array.from({ length: binCount }, () => 0);
	values.forEach((value) => {
		const index = Math.min(binCount - 1, Math.floor((value - lowerLimit) / binWidth));
		counts[index] = counts[index]! + 1;
	});
	return {
		bins: counts.map((count, index): [number, number] => [
			lowerLimit + binWidth * (index + 0.5),
			count / values.length,
		]),
		binWidth,
	};
}

function calculateAndersonDarling(values: number[], mean: number, standardDeviation: number): [number, number] {
	if (standardDeviation === 0) return [Number.NaN, Number.NaN];
	const n = values.length;
	const epsilon = 1e-15;
	const sum = values.reduce((total, value, index) => {
		const lowerCdf = clamp(normalCdf((value - mean) / standardDeviation), epsilon, 1 - epsilon);
		const upperValue = values[n - 1 - index]!;
		const upperCdf = clamp(normalCdf((upperValue - mean) / standardDeviation), epsilon, 1 - epsilon);
		return total + (2 * (index + 1) - 1) * (Math.log(lowerCdf) + Math.log(1 - upperCdf));
	}, 0);
	const statistic = -n - sum / n;
	const adjusted = statistic * (1 + 0.75 / n + 2.25 / n ** 2);
	const pValue =
		adjusted >= 0.6 ? Math.exp(1.2937 - 5.709 * adjusted + 0.0186 * adjusted ** 2)
		: adjusted >= 0.34 ? Math.exp(0.9177 - 4.279 * adjusted - 1.38 * adjusted ** 2)
		: adjusted > 0.2 ? 1 - Math.exp(-8.318 + 42.796 * adjusted - 59.938 * adjusted ** 2)
		: 1 - Math.exp(-13.436 + 101.14 * adjusted - 223.73 * adjusted ** 2);
	return [statistic, pValue];
}

function normalCdf(value: number): number {
	return 0.5 * (1 + erf(value / Math.sqrt(2)));
}

function erf(value: number): number {
	const sign = value < 0 ? -1 : 1;
	const absolute = Math.abs(value);
	const t = 1 / (1 + 0.3275911 * absolute);
	const approximation =
		1 -
		((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
			t *
			Math.exp(-(absolute ** 2));
	return sign * approximation;
}

function quantile(sortedValues: number[], probability: number): number {
	const index = (sortedValues.length - 1) * probability;
	const lower = Math.floor(index);
	const fraction = index - lower;
	return (
		sortedValues[lower]! +
		(sortedValues[Math.min(lower + 1, sortedValues.length - 1)]! - sortedValues[lower]!) * fraction
	);
}

function minimumNullable(first: number | null, second: number | null): number | null {
	if (first == null) return second;
	if (second == null) return first;
	return Math.min(first, second);
}

function average(values: number[]): number {
	return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(maximum, Math.max(minimum, value));
}
